import PropTypes from 'prop-types'
import React from 'react'
import times from 'lodash/times'
import {css} from 'glamor'

import Badge from './badge'
import {colors, palettes} from '../styles'

const states = {
  active: {
    label: css({
      background: palettes.secondary.base,
      color: palettes.secondary.text
    }),
    minLabel: css({
      color: colors.mutedText
    }),
    maxLabel: css({
      color: colors.mutedText
    }),
    range: css({
      '&::-webkit-slider-thumb': {
        background: palettes.secondary.base,
        borderColor: palettes.secondary.base,
        boxShadow: `0px 0px 4px ${palettes.secondary.base}`
      },
      '&::-moz-range-thumb': {
        background: palettes.secondary.base,
        borderColor: palettes.secondary.base,
        boxShadow: `0px 0px 4px ${palettes.secondary.base}`
      }
    })
  },
  disabled: {
    label: css({
      background: colors.gray89,
      color: palettes.secondary.text
    }),
    minLabel: css({
      color: colors.gray77
    }),
    maxLabel: css({
      color: colors.gray77
    }),
    range: css({
      '&::-webkit-slider-thumb': {
        background: colors.gray89,
        borderColor: colors.gray89,
        boxShadow: `0px 0px 4px ${colors.gray77}`
      },
      '&::-moz-range-thumb': {
        background: colors.gray89,
        borderColor: colors.gray89,
        boxShadow: `0px 0px 4px ${colors.gray77}`
      }
    })
  }
}

const elements = {
  thumb: css({
    borderRadius: '10px',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxShadow: `0px 0px 4px ${palettes.secondary.base}`,
    cursor: 'pointer',
    position: 'relative',
    height: '20px',
    width: '20px',
    zIndex: 100
  }),
  track: css({
    background: colors.gray97,
    borderRadius: '10px',
    boxShadow: `inset 0px 0px 3px ${colors.gray89}`,
    cursor: 'pointer',
    height: '12px',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 0
  })
}

const style = {
  container: css({
    overflow: 'auto',
    textAlign: 'center'
  }),
  field: css({
    position: 'relative'
  }),
  marker: css({
    borderRight: '1px solid #ccc',
    height: '12px',
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    zIndex: 1
  }),
  minLabel: css({
    float: 'left',
    margin: '8px 0 0'
  }),
  maxLabel: css({
    float: 'right',
    margin: '8px 0 0'
  }),
  range: css({
    // General styles
    WebkitAppearance: 'none',
    position: 'relative',
    margin: '10px 0',
    width: '100%',
    zIndex: 10,
    '&:focus': {
      outline: 'none',
      '& + div': {
        boxShadow: `inset 0px 0px 4px ${colors.gray77}`
      }
    },

    // Brower-specific resets
    '&::-moz-focus-outer': {
      border: 'none'
    },

    // Slider track styles
    '&::-webkit-slider-runnable-track': {
      background: 'transparent',
      height: 0
    },
    '&::-moz-range-track': {
      background: 'transparent',
      height: 0
    },

    // Thumb styles
    '&::-webkit-slider-thumb': css(elements.thumb, {
      WebkitAppearance: 'none',
      marginTop: '-4px'
    }),
    '&::-moz-range-thumb': elements.thumb
  }),
  wrapper: css({
    position: 'relative'
  })
}

const Slider = ({
  disabled,
  label,
  max,
  maxLabel,
  min,
  minLabel,
  onChange,
  readOnly,
  style: customStyle,
  value
}) => {
  const state = states[disabled ? 'disabled' : 'active']
  return (
    <div {...css(style.container, customStyle)}>
      {label && <Badge style={css(style.label, state.label)}>{label}</Badge>}
      <div {...style.field}>
        <div {...style.wrapper}>
          <input
            disabled={disabled}
            max={max}
            min={min}
            onChange={onChange}
            readOnly={readOnly}
            type="range"
            value={value}
            {...css(style.range, state.range)}
          />
          <div {...elements.track} />
        </div>
        {times(max - min - 1, (index) => (
          <div
            key={index}
            {...css(style.marker, {
              /*
               * When the thumb marker on a range slider is rendered, it doesn't go to the farthest
               * edges of the input, leaving about 2% of the input unused on either end. To account
               * for this with our tick marks, we divide our tick marks on a 96% base rather an
               * 100% base.
               */
              left: `${(96 / (max - min)) * (index + 1) + 2}%`
            })}
          />
        ))}
        {minLabel && (
          <span {...css(style.minLabel, state.minLabel)}>{minLabel}</span>
        )}
        {maxLabel && (
          <span {...css(style.maxLabel, state.maxLabel)}>{maxLabel}</span>
        )}
      </div>
    </div>
  )
}

Slider.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string,
  max: PropTypes.number.isRequired,
  maxLabel: PropTypes.string,
  min: PropTypes.number.isRequired,
  minLabel: PropTypes.string,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.number
}

Slider.defaultProps = {
  disabled: false,
  readOnly: false
}

export default Slider
